<template>
    <div tabindex="0" class="no-focus">
        <div class="flex-1 bg-custom-gray-100 border border-custom-gray-100 rounded-lg no-focus">
            <Label class="pt-1.5 px-3">{{ $t('Where?') }}</Label>
            <MultiSelect
                size="lg"
                variant="form"
                :object="true"
                v-model="modelValue"
                :mode="mode"
                :options="
                    async function (query, select$) {
                        return await fetchList(query) // check JS block in JSFiddle for implementation
                    }
                "
                :delay="300"
                :searchable="true"
                :filter-results="false"
                :min-chars="1"
                label="name"
                valueProp="url"
                trackBy="name"
                :placeholder="$t('Start typing to search...')"
                :closeOnSelect="closeOnSelect"
                :resolve-on-load="false"
                :groups="true"
                @select="select"
            />
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { useAsyncRequest } from '@/composables/useAsyncRequest'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const { request } = useAsyncRequest()

// SEO listing path of the selected suggestion — used to navigate straight to
// /nastanitve/{url} instead of a /ponudbe query search.
const url = defineModel('url')
const q = defineModel('q')

const modelValue = computed({
    get() {
        if (!url.value) return null

        return { url: url.value, name: q.value }
    },

    set(val) {
        // user cleared selection
        if (!val) {
            url.value = null
            q.value = ''
            return
        }

        // user typed directly or picked a suggestion from the multiselect
        q.value = val.name
        url.value = val.url ?? null
    },
})
// Props
defineProps({
    mode: { type: String, default: 'single' },
    closeOnSelect: { type: Boolean, default: true },
})

const select = (event) => {
    url.value = event.url ?? null
    q.value = event.name
}

// Fetch async grouped results
const fetchList = async (query) => {
    try {
        const response = await request('get', '/api/quicksearch', {
            'filter[q]': query,
        })

        const joinParts = (...parts) => parts.filter(Boolean).join(', ')

        return [
            {
                label: t('Properties'),
                options: (response.properties || []).map((item) => ({
                    name: joinParts(item.name, item.location?.name, item.location?.region?.name, item.location?.region?.regionGroup?.name),
                    url: item.url,
                })),
            },
            {
                label: t('Locations'),
                options: (response.locations || []).map((item) => ({
                    name: joinParts(item.name, item.region?.name, item.region?.regionGroup?.name),
                    url: item.url,
                })),
            },
            {
                label: t('Regions'),
                options: (response.regions || []).map((item) => ({
                    name: joinParts(item.name, item.regionGroup?.name),
                    url: item.url,
                })),
            },
            {
                label: t('Countries'),
                options: (response.groups || []).map((item) => ({
                    name: item.name,
                    url: item.url,
                })),
            },
        ]
    } catch (error) {
        return [
            { label: t('Properties'), options: [] },
            { label: t('Locations'), options: [] },
            { label: t('Regions'), options: [] },
            { label: t('Countries'), options: [] },
        ]
    }
}
</script>
